import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import GlowButton from '@/components/base/GlowButton';

function GlobalSearchBox() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const boxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 60);
    }
  }, [open]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) {
        setOpen(false);
        setValue('');
      }
    };
    if (open) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const q = value.trim();
    if (q) {
      navigate(`/search?q=${encodeURIComponent(q)}`);
      setOpen(false);
      setValue('');
    }
  };

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="w-8 h-8 flex items-center justify-center rounded-full bg-white/30 hover:bg-white/55 backdrop-blur-sm border border-[#b8d4ea]/40 hover:border-[#b8d4ea]/70 text-slate-500 hover:text-slate-800 transition-all cursor-pointer shrink-0"
        title="Search articles"
      >
        <i className="ri-search-line text-sm"></i>
      </button>
    );
  }

  return (
    <div ref={boxRef} className="relative shrink-0">
      <form onSubmit={handleSubmit}>
        <div className="flex items-center bg-white/80 backdrop-blur-md rounded-full px-3 py-1.5 border border-[#0088CC]/40 gap-2 w-56 transition-all">
          <i className="ri-search-line text-[#0088CC] text-sm shrink-0"></i>
          <input
            ref={inputRef}
            type="text"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder="Search articles..."
            className="flex-1 bg-transparent text-sm text-slate-700 placeholder-slate-400 outline-none min-w-0"
          />
          {value && (
            <button
              type="button"
              onClick={() => setValue('')}
              className="w-4 h-4 flex items-center justify-center shrink-0 text-slate-400 hover:text-slate-600 cursor-pointer"
            >
              <i className="ri-close-line text-xs"></i>
            </button>
          )}
        </div>
      </form>
    </div>
  );
}

const rankingItems = [
  { key: 'monthly', label: 'Top By Monthly', icon: 'ri-calendar-line' },
  { key: 'categories', label: 'Top By Categories', icon: 'ri-apps-2-line' },
  { key: 'regions', label: 'Top By Regions', icon: 'ri-map-pin-line' },
  { key: 'source', label: 'Top By Source', icon: 'ri-share-forward-line' },
  { key: 'revenue', label: 'Top By Revenue', icon: 'ri-money-dollar-circle-line' },
];

interface SiteHeaderProps {
  /** 首页需要搜索栏 */
  searchQuery?: string;
  onSearchChange?: (q: string) => void;
  onMobileMenuToggle?: () => void;
  onMobileFilterToggle?: () => void;
  /** 非首页显示返回按钮 */
  showBack?: boolean;
  /** 返回按钮跳转的路径，默认 '/' */
  backTo?: string;
}

const navLinks = [
  { label: 'Home', href: '/' },
  { label: 'Directory', href: '#', hasDropdown: 'directory' as const },
  { label: 'Ranking', href: '#', hasDropdown: 'ranking' as const },
  { label: 'Blogs', href: '/blogs' },
  { label: 'Submit', href: '#' },
  { label: 'Contact', href: '/contact' },
];

const directoryCategories = [
  { label: 'IT & Technology', icon: 'ri-code-s-slash-line', category: 'IT' },
  { label: 'Finance & Crypto', icon: 'ri-line-chart-line', category: 'Finance' },
  { label: 'Games & Entertainment', icon: 'ri-gamepad-line', category: 'Games' },
  { label: 'Health & Wellness', icon: 'ri-heart-pulse-line', category: 'Health' },
  { label: 'Arts & Creative', icon: 'ri-palette-line', category: 'Arts' },
  { label: 'Education', icon: 'ri-book-open-line', category: 'Education' },
  { label: 'Business', icon: 'ri-briefcase-line', category: 'Business' },
];

const languages = [
  { code: 'EN', label: 'English', flag: '🇺🇸' },
  { code: 'ZH', label: '中文', flag: '🇨🇳' },
  { code: 'ES', label: 'Español', flag: '🇪🇸' },
  { code: 'RU', label: 'Русский', flag: '🇷🇺' },
  { code: 'AR', label: 'العربية', flag: '🇸🇦' },
  { code: 'PT', label: 'Português', flag: '🇧🇷' },
  { code: 'DE', label: 'Deutsch', flag: '🇩🇪' },
  { code: 'JA', label: '日本語', flag: '🇯🇵' },
];

export default function SiteHeader({
  searchQuery = '',
  onSearchChange,
  onMobileMenuToggle,
  onMobileFilterToggle,
  showBack = false,
  backTo = '/',
}: SiteHeaderProps) {
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<'directory' | 'ranking' | null>(null);
  const [langOpen, setLangOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchFocused, setSearchFocused] = useState(false);
  const [selectedLang, setSelectedLang] = useState(languages[0]);

  const langRef = useRef<HTMLDivElement>(null);
  const dirRef = useRef<HTMLDivElement>(null);
  const rankRef = useRef<HTMLDivElement>(null);
  const hoverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const openDropdownHover = useCallback((key: 'directory' | 'ranking') => {
    if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
    setOpenDropdown(key);
  }, []);

  const closeDropdownHover = useCallback(() => {
    hoverTimerRef.current = setTimeout(() => setOpenDropdown(null), 120);
  }, []);

  const cancelCloseDropdown = useCallback(() => {
    if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
  }, []);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (langRef.current && !langRef.current.contains(e.target as Node)) setLangOpen(false);
      if (dirRef.current && !dirRef.current.contains(e.target as Node) &&
          rankRef.current && !rankRef.current.contains(e.target as Node)) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
    onMobileMenuToggle?.();
  };

  return (
    <header
      className={`sticky top-0 z-50 w-full transition-all duration-300 ${
        scrolled
          ? 'bg-[#ddeaf5]/80 backdrop-blur-xl border-b border-[#b8d4ea]/50'
          : 'bg-[#e4eef8]/60 backdrop-blur-lg border-b border-[#c5d9eb]/40'
      }`}
    >
      <div className="max-w-screen-2xl mx-auto px-6 md:px-10 lg:px-14 h-16 flex items-center gap-3">
        {/* Logo */}
        <a
          href="/"
          onClick={(e) => { e.preventDefault(); navigate('/'); }}
          className="flex items-center gap-2.5 shrink-0 cursor-pointer"
        >
          <img
            src="https://public.readdy.ai/ai/img_res/d5d862b0-845f-46d1-8b65-431c8335c5b6.png"
            alt="TGDirectory Logo"
            className="w-8 h-8 object-contain"
          />
          <span className="text-slate-800 font-bold text-lg hidden sm:block whitespace-nowrap">
            TG<span className="text-[#0088CC]">Directory</span>
          </span>
        </a>

        {/* Back button — only on inner pages */}
        {showBack && (
          <button
            onClick={() => navigate(backTo)}
            className="hidden md:flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 bg-white/40 hover:bg-white/65 backdrop-blur-sm border border-[#b8d4ea]/40 hover:border-[#b8d4ea]/70 px-3 py-1.5 rounded-lg transition-all cursor-pointer whitespace-nowrap shrink-0"
          >
            <i className="ri-arrow-left-line text-sm w-4 h-4 flex items-center justify-center"></i>
            Back
          </button>
        )}

        {/* Search Bar — only on home */}
        {onSearchChange && (
          <div
            className={`flex-1 max-w-[140px] sm:max-w-sm lg:max-w-xl mx-auto relative transition-all duration-200 ${
              searchFocused ? 'max-w-[200px] sm:max-w-xl lg:max-w-2xl' : ''
            }`}
          >
            <div
              className={`flex items-center bg-white/55 backdrop-blur-sm rounded-full px-3 md:px-4 py-1.5 md:py-2 border transition-all duration-200 ${
                searchFocused ? 'border-[#0088CC]/50 bg-white/80' : 'border-[#b8d4ea]/60'
              }`}
            >
              <i className="ri-search-line text-slate-400 text-sm mr-1.5 md:mr-2 w-4 h-4 flex items-center justify-center shrink-0"></i>
              <input
                type="text"
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => onSearchChange(e.target.value)}
                onFocus={() => setSearchFocused(true)}
                onBlur={() => setSearchFocused(false)}
                className="bg-transparent text-sm text-slate-700 placeholder-slate-400 outline-none flex-1 min-w-0 w-full"
              />
              {searchQuery && (
                <button
                  onClick={() => onSearchChange('')}
                  className="text-slate-400 hover:text-slate-600 cursor-pointer ml-1 w-4 h-4 flex items-center justify-center shrink-0"
                >
                  <i className="ri-close-line text-sm"></i>
                </button>
              )}
            </div>
          </div>
        )}

        {/* Spacer when no search bar */}
        {!onSearchChange && <div className="flex-1" />}

        {/* Desktop Nav */}
        <nav className="hidden lg:flex items-center gap-0.5">
          {navLinks.map((link) => (
            <div
              key={link.label}
              className="relative"
              ref={link.hasDropdown === 'directory' ? dirRef : link.hasDropdown === 'ranking' ? rankRef : undefined}
              onMouseEnter={link.hasDropdown ? () => openDropdownHover(link.hasDropdown as 'directory' | 'ranking') : undefined}
              onMouseLeave={link.hasDropdown ? closeDropdownHover : undefined}
            >
              {link.hasDropdown ? (
                <button
                  className="flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900 px-3 py-2 rounded-lg bg-transparent hover:bg-white/40 hover:backdrop-blur-sm border border-transparent hover:border-[#b8d4ea]/40 transition-all cursor-pointer whitespace-nowrap"
                >
                  {link.label}
                  <i className={`ri-arrow-down-s-line text-xs transition-transform duration-200 ${openDropdown === link.hasDropdown ? 'rotate-180' : ''}`}></i>
                </button>
              ) : (
                <a
                  href={link.href}
                  onClick={(e) => {
                    e.preventDefault();
                    if (link.label === 'Submit') {
                      window.dispatchEvent(new CustomEvent('open-submit-modal'));
                    } else if (link.href !== '#') {
                      navigate(link.href);
                    }
                  }}
                  className="text-sm text-slate-600 hover:text-slate-900 px-3 py-2 rounded-lg bg-transparent hover:bg-white/40 hover:backdrop-blur-sm border border-transparent hover:border-[#b8d4ea]/40 transition-all whitespace-nowrap cursor-pointer"
                >
                  {link.label}
                </a>
              )}

              {/* Directory Dropdown */}
              {link.hasDropdown === 'directory' && openDropdown === 'directory' && (
                <div
                  className="absolute top-full left-0 mt-1 w-56 bg-[#ddeaf5]/95 backdrop-blur-xl border border-[#b8d4ea]/50 rounded-xl py-2 z-50"
                  onMouseEnter={cancelCloseDropdown}
                  onMouseLeave={closeDropdownHover}
                >
                  {directoryCategories.map((cat) => (
                    <button
                      key={cat.label}
                      onClick={() => { navigate(`/category/${cat.category}`); setOpenDropdown(null); }}
                      className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-slate-600 hover:text-slate-900 hover:bg-white/50 transition-colors cursor-pointer text-left"
                    >
                      <span className="w-4 h-4 flex items-center justify-center text-[#0088CC] shrink-0">
                        <i className={`${cat.icon} text-sm`}></i>
                      </span>
                      {cat.label}
                    </button>
                  ))}
                </div>
              )}

              {/* Ranking Dropdown */}
              {link.hasDropdown === 'ranking' && openDropdown === 'ranking' && (
                <div
                  className="absolute top-full left-0 mt-1 w-52 bg-[#ddeaf5]/95 backdrop-blur-xl border border-[#b8d4ea]/50 rounded-xl py-2 z-50"
                  onMouseEnter={cancelCloseDropdown}
                  onMouseLeave={closeDropdownHover}
                >
                  {rankingItems.map((item) => (
                    <button
                      key={item.label}
                      onClick={() => { navigate(`/ranking/${item.key}`); setOpenDropdown(null); }}
                      className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-slate-600 hover:text-slate-900 hover:bg-white/50 transition-colors cursor-pointer text-left"
                    >
                      <span className="w-4 h-4 flex items-center justify-center text-[#0088CC] shrink-0">
                        <i className={`${item.icon} text-sm`}></i>
                      </span>
                      {item.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}
        </nav>

        {/* Right Controls */}
        <div className="hidden md:flex items-center gap-2 shrink-0">
          {/* Global Search — shown on non-home pages (showBack mode) */}
          {showBack && <GlobalSearchBox />}
          {/* Language Switcher */}
          <div
            className="relative"
            ref={langRef}
            onMouseEnter={() => { if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current); setLangOpen(true); }}
            onMouseLeave={() => { hoverTimerRef.current = setTimeout(() => setLangOpen(false), 120); }}
          >
            <button
              className="flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900 px-3 py-2 rounded-full bg-white/30 hover:bg-white/55 backdrop-blur-sm border border-[#b8d4ea]/40 hover:border-[#b8d4ea]/70 transition-all cursor-pointer whitespace-nowrap"
            >
              <span className="text-base leading-none">{selectedLang.flag}</span>
              <span className="text-xs font-semibold">{selectedLang.code}</span>
              <i
                className={`ri-arrow-down-s-line text-xs transition-transform duration-200 ${
                  langOpen ? 'rotate-180' : ''
                }`}
              ></i>
            </button>

            {langOpen && (
              <div className="absolute top-full right-0 mt-1 w-44 bg-[#ddeaf5]/95 backdrop-blur-xl border border-[#b8d4ea]/50 rounded-xl py-2 z-50">
                {languages.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => {
                      setSelectedLang(lang);
                      setLangOpen(false);
                    }}
                    className={`w-full flex items-center gap-3 px-4 py-2 text-sm transition-colors cursor-pointer ${
                      selectedLang.code === lang.code
                        ? 'text-[#0088CC] bg-[#0088CC]/8 font-semibold'
                        : 'text-slate-600 hover:text-slate-900 hover:bg-white/50'
                    }`}
                  >
                    <span className="text-base leading-none">{lang.flag}</span>
                    <span className="flex-1 text-left">{lang.label}</span>
                    {selectedLang.code === lang.code && (
                      <span className="w-4 h-4 flex items-center justify-center shrink-0">
                        <i className="ri-check-line text-[#0088CC] text-xs"></i>
                      </span>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>

          <GlowButton className="text-sm px-4 py-2 rounded-full">Login</GlowButton>
        </div>

        {/* Mobile Controls */}
        <div className="flex md:hidden items-center gap-1.5 ml-auto shrink-0">
          {showBack && (
            <button
              onClick={() => navigate(backTo)}
              className="w-8 h-8 flex items-center justify-center text-slate-500 hover:text-slate-800 bg-white/40 backdrop-blur-sm border border-[#b8d4ea]/50 rounded-lg cursor-pointer"
            >
              <i className="ri-arrow-left-line text-sm"></i>
            </button>
          )}
          {showBack && (
            <button
              onClick={() => navigate('/search')}
              className="w-8 h-8 flex items-center justify-center text-slate-500 hover:text-slate-800 bg-white/40 backdrop-blur-sm border border-[#b8d4ea]/50 rounded-lg cursor-pointer"
            >
              <i className="ri-search-line text-sm"></i>
            </button>
          )}
          {onMobileFilterToggle && (
            <button
              onClick={onMobileFilterToggle}
              className="w-8 h-8 flex items-center justify-center text-slate-500 hover:text-slate-800 bg-white/40 backdrop-blur-sm border border-[#b8d4ea]/50 rounded-lg cursor-pointer"
            >
              <i className="ri-filter-3-line text-sm"></i>
            </button>
          )}
          <button
            onClick={handleMobileMenu}
            className="w-8 h-8 flex items-center justify-center text-slate-500 hover:text-slate-800 bg-white/40 backdrop-blur-sm border border-[#b8d4ea]/50 rounded-lg cursor-pointer"
          >
            <i className={`text-sm ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-[#ddeaf5]/90 backdrop-blur-xl border-t border-[#b8d4ea]/50 px-4 py-4 flex flex-col gap-1">
          {navLinks.map((link) =>
            link.hasDropdown === 'directory' ? (
              <div key={link.label}>
                <div className="text-xs font-semibold text-slate-400 uppercase tracking-wide px-3 pt-2 pb-1">
                  Directory
                </div>
                {directoryCategories.map((cat) => (
                  <button
                    key={cat.label}
                    onClick={() => { navigate(`/category/${cat.category}`); setMobileMenuOpen(false); }}
                    className="w-full flex items-center gap-3 px-3 py-2 text-sm text-slate-600 hover:text-slate-900 rounded-lg hover:bg-white/50 transition-colors cursor-pointer text-left"
                  >
                    <span className="w-4 h-4 flex items-center justify-center text-[#0088CC] shrink-0">
                      <i className={`${cat.icon} text-sm`}></i>
                    </span>
                    {cat.label}
                  </button>
                ))}
              </div>
            ) : link.hasDropdown === 'ranking' ? (
              <div key={link.label}>
                <div className="text-xs font-semibold text-slate-400 uppercase tracking-wide px-3 pt-2 pb-1">
                  Ranking
                </div>
                {rankingItems.map((item) => (
                  <button
                    key={item.label}
                    onClick={() => { navigate(`/ranking/${item.key}`); setMobileMenuOpen(false); }}
                    className="w-full flex items-center gap-3 px-3 py-2 text-sm text-slate-600 hover:text-slate-900 rounded-lg hover:bg-white/50 transition-colors cursor-pointer text-left"
                  >
                    <span className="w-4 h-4 flex items-center justify-center text-[#0088CC] shrink-0">
                      <i className={`${item.icon} text-sm`}></i>
                    </span>
                    {item.label}
                  </button>
                ))}
              </div>
            ) : (
              <a
                key={link.label}
                href={link.href}
                onClick={(e) => {
                  e.preventDefault();
                  if (link.label === 'Submit') {
                    window.dispatchEvent(new CustomEvent('open-submit-modal'));
                    setMobileMenuOpen(false);
                  } else if (link.href !== '#') {
                    navigate(link.href);
                    setMobileMenuOpen(false);
                  }
                }}
                className="text-sm text-slate-600 hover:text-slate-900 px-3 py-2.5 rounded-lg hover:bg-slate-50 transition-all cursor-pointer"
              >
                {link.label}
              </a>
            )
          )}
          <div className="flex gap-2 mt-3 pt-3 border-t border-[#b8d4ea]/40">
            <div className="flex items-center gap-1.5 px-3 py-2 rounded-full bg-white/40 border border-[#b8d4ea]/50 text-sm text-slate-600">
              <span>{selectedLang.flag}</span>
              <span className="text-xs font-semibold">{selectedLang.code}</span>
            </div>
            <GlowButton className="flex-1 text-sm py-2 rounded-full">Login</GlowButton>
          </div>
        </div>
      )}
    </header>
  );
}
